/**
 * Typography defaults and HTML builder for Work Format (copy with formatting).
 * All numeric sizes support 2 decimal places; stored as number.
 */
import { convertUnicodeToLegacy, TAMIL_FONTS } from "./utils/tamil_converter";

const STORAGE_PREFIX = "work-format-typo-";

export const DEFAULT_TYPOGRAPHY = {
  fontFamily: "Times New Roman",
  fontSizePt: 10,
  fontWeight: "normal", // normal | bold
  fontStyle: "normal", // normal | italic
  letterSpacingPt: 0,
  wordSpacingPt: 0,
  lineHeight: 1.4,
  textTransform: "none", // none | uppercase | lowercase | capitalize | small-caps
  underline: false,
  strikethrough: false,
  color: "#000000",
  backgroundColor: "transparent",
  marginBeforePt: 0,
  marginAfterPt: 4,
  sectionTitleFontSizePt: 11,
  sectionTitleBold: true,
  dateFontSizePt: 12,
  dateBold: true,
  timingFontSizePt: 10, // same as body by default
  horizontalScalePct: 100,
  baselineShiftPt: 0,
  superscript: false,
  subscript: false,
  copyFormat: "html", // "plain" | "html" — when "html", write text/html to clipboard
  encoding: "Unicode", // "Unicode" | "TAM" | "TAB" | "TSCII"
};


const keys = Object.keys(DEFAULT_TYPOGRAPHY);

export function loadTypographyFromStorage() {
  const out = { ...DEFAULT_TYPOGRAPHY };
  try {
    keys.forEach((key) => {
      const raw = localStorage.getItem(STORAGE_PREFIX + key);
      if (raw == null) return;
      const def = DEFAULT_TYPOGRAPHY[key];
      if (typeof def === "number") {
        const n = parseFloat(raw);
        if (Number.isFinite(n)) {
          if (key.includes("Pt") || key === "lineHeight" || key === "horizontalScalePct") out[key] = Math.round(n * 100) / 100;
          else out[key] = n;
        }
      } else if (typeof def === "boolean") {
        out[key] = raw === "true";
      } else if (typeof def === "string") {
        out[key] = raw;
      }
    });
  } catch (_) {}
  return out;
}

export function saveTypographyToStorage(typo) {
  try {
    keys.forEach((key) => {
      localStorage.setItem(STORAGE_PREFIX + key, String(typo[key]));
    });
  } catch (_) {}
}

/** Inline style object for body/line text (React) */
export function buildBodyStyle(typo) {
  const fontSizePt = typo.fontSizePt ?? DEFAULT_TYPOGRAPHY.fontSizePt;
  return {
    fontFamily: typo.fontFamily,
    fontSize: `${fontSizePt}pt`,
    fontWeight: typo.fontWeight,
    fontStyle: typo.fontStyle,
    letterSpacing: typo.letterSpacingPt != null && typo.letterSpacingPt !== 0 ? `${typo.letterSpacingPt}pt` : undefined,
    wordSpacing: typo.wordSpacingPt != null && typo.wordSpacingPt !== 0 ? `${typo.wordSpacingPt}pt` : undefined,
    lineHeight: typo.lineHeight,
    textTransform: typo.textTransform !== "none" ? typo.textTransform : undefined,
    textDecoration: [typo.underline && "underline", typo.strikethrough && "line-through"].filter(Boolean).join(" ") || undefined,
    color: typo.color,
    backgroundColor: typo.backgroundColor !== "transparent" ? typo.backgroundColor : undefined,
    marginTop: typo.marginBeforePt != null && typo.marginBeforePt > 0 ? `${typo.marginBeforePt}pt` : undefined,
    marginBottom: typo.marginAfterPt != null && typo.marginAfterPt > 0 ? `${typo.marginAfterPt}pt` : undefined,
    transform: typo.horizontalScalePct !== 100 ? `scaleX(${typo.horizontalScalePct / 100})` : undefined,
    verticalAlign: typo.baselineShiftPt !== 0 ? `${typo.baselineShiftPt}pt` : typo.superscript ? "super" : typo.subscript ? "sub" : undefined,
    fontVariant: typo.textTransform === "small-caps" ? "small-caps" : undefined,
  };
}

/** Inline style for section titles (e.g. "Panchang", "Rashi") */
export function buildSectionTitleStyle(typo) {
  return {
    fontFamily: typo.fontFamily,
    fontSize: `${typo.sectionTitleFontSizePt ?? typo.fontSizePt}pt`,
    fontWeight: typo.sectionTitleBold ? "bold" : typo.fontWeight,
    fontStyle: typo.fontStyle,
    color: typo.color,
    marginTop: typo.marginBeforePt != null && typo.marginBeforePt > 0 ? `${typo.marginBeforePt}pt` : undefined,
    marginBottom: typo.marginAfterPt != null && typo.marginAfterPt > 0 ? `${typo.marginAfterPt}pt` : undefined,
  };
}

/** Inline style for date/header row */
export function buildDateStyle(typo) {
  return {
    fontFamily: typo.fontFamily,
    fontSize: `${typo.dateFontSizePt ?? typo.fontSizePt}pt`,
    fontWeight: typo.dateBold ? "bold" : typo.fontWeight,
    fontStyle: typo.fontStyle,
    color: typo.color,
  };
}

/** Build CSS string for HTML paste (inline styles) */
function styleToString(styleObj) {
  return Object.entries(styleObj)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => {
      const prop = k.replace(/([A-Z])/g, "-$1").toLowerCase().replace(/^-/, "");
      return `${prop}: ${v}`;
    })
    .join("; ");
}

/** Escape for HTML text content */
function esc(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Build HTML for one day's content (for clipboard text/html) */
export function getDateAsHtml(entry, spacingConfig, typography, buildLine, formatMuhurta, showMidnight = true) {
  const bodyStyle = styleToString(buildBodyStyle(typography));
  const sectionTitleStyle = styleToString(buildSectionTitleStyle(typography));
  const dateStyle = styleToString(buildDateStyle(typography));

  const sections = [
    ["header", "Header"],
    ["panchang", "Panchang"],
    ["rashi", "Rashi"],
    ["muhurta", "Favorable timings"],
    ["activities", "Activities"],
    ["inauspicious", "Inauspicious"],
  ];

  const conv = (txt) => convertUnicodeToLegacy(txt, typography.encoding);
  const line = (label, time) => buildLine(conv(label), time, spacingConfig, showMidnight);

  const parts = [];

  for (const [key, title] of sections) {
    let html = "";
    if (key === "header") {
      html += `<p style="${dateStyle}">${esc(conv(entry.date))} — ${esc(conv(entry.weekday))}</p>`;
      html += `<p style="${bodyStyle}">Rahukalam: ${esc(entry.rahukalam || "—")}</p>`;
      html += `<p style="${bodyStyle}">Yamagandam: ${esc(entry.yamagandam || "—")}</p>`;
    } else if (key === "panchang") {
      html += `<p style="${sectionTitleStyle}">${esc(title)}</p>`;
      [
        ...(entry.nakshatra || []).map(([name, t]) => line(name, t)),
        ...(entry.tithi || []).map(([name, t]) => line(name, t)),
        ...(entry.yoga || []).map(([name, t]) => line(name, t)),
        ...(entry.karana || []).map(([name, t]) => line(name, t)),
      ].forEach((text) => {
        html += `<p style="${bodyStyle}">${esc(text)}</p>`;
      });
    } else if (key === "rashi" && (entry.rashi || []).length > 0) {
      html += `<p style="${sectionTitleStyle}">${esc(title)}</p>`;
      (entry.rashi || []).forEach(([name, t]) => {
        html += `<p style="${bodyStyle}">${esc(line(name, t))}</p>`;
      });
    } else if (key === "muhurta") {
      const arr = formatMuhurta ? formatMuhurta(entry.muhurta, showMidnight) : (() => {
        if (!entry.muhurta || typeof entry.muhurta !== "object") return [];
        const entries = Object.entries(entry.muhurta).reduce((acc, [k, value]) => {
          const idx = k.match(/\d+/)?.[0];
          if (!idx) return acc;
          if (!acc[idx]) acc[idx] = {};
          acc[idx][k.includes("start") ? "start" : "end"] = value;
          return acc;
        }, {});
        const fmt = (t) => (t && t !== "00:00" ? t : showMidnight ? "00:00" : "");
        return Object.entries(entries)
          .sort((a, b) => Number(a[0]) - Number(b[0]))
          .map(([, { start, end }]) => `${fmt(start)} - ${end || "24:00"}`);
      })();
      html += `<p style="${sectionTitleStyle}">${esc(title)}</p>`;
      if (arr.length) arr.forEach((s) => { html += `<p style="${bodyStyle}">${esc(s)}</p>`; });
      else html += `<p style="${bodyStyle}">No favourable timings</p>`;
    } else if (key === "activities" && entry.activities?.length) {
      html += `<p style="${sectionTitleStyle}">${esc(title)}</p>`;
      entry.activities.forEach((a) => {
        const text = typeof a === "string" ? a : String(a?.join?.(" ") ?? a);
        html += `<p style="${bodyStyle}">${esc(conv(text))}</p>`;
      });
    } else if (key === "inauspicious" && entry.inauspicious) {
      html += `<p style="${sectionTitleStyle}">${esc(title)}</p>`;
      html += `<p style="${bodyStyle}">${esc(conv(entry.inauspicious))}</p>`;
    }
    if (html) parts.push(html);
  }


  return `<div style="font-family: ${esc(typography.fontFamily)};">${parts.join("")}</div>`;
}

/** Build HTML for a single section (for section copy with formatting) */
export function getSectionAsHtml(entry, sectionKey, spacingConfig, typography, buildLine, formatMuhurta, showMidnight = true) {
  const bodyStyle = styleToString(buildBodyStyle(typography));
  const dateStyle = styleToString(buildDateStyle(typography));

  const conv = (txt) => convertUnicodeToLegacy(txt, typography.encoding);
  const line = (label, time) => buildLine(conv(label), time, spacingConfig, showMidnight);
  let html = "";

  if (sectionKey === "header") {
    html += `<p style="${dateStyle}">${esc(conv(entry.date))} — ${esc(conv(entry.weekday))}</p>`;
    html += `<p style="${bodyStyle}">Rahukalam: ${esc(entry.rahukalam || "—")}</p>`;
    html += `<p style="${bodyStyle}">Yamagandam: ${esc(entry.yamagandam || "—")}</p>`;
  } else if (sectionKey === "panchang") {
    [
      ...(entry.nakshatra || []).map(([name, t]) => line(name, t)),
      ...(entry.tithi || []).map(([name, t]) => line(name, t)),
      ...(entry.yoga || []).map(([name, t]) => line(name, t)),
      ...(entry.karana || []).map(([name, t]) => line(name, t)),
    ].forEach((text) => { html += `<p style="${bodyStyle}">${esc(text)}</p>`; });
  } else if (sectionKey === "rashi") {
    (entry.rashi || []).forEach(([name, t]) => { html += `<p style="${bodyStyle}">${esc(line(name, t))}</p>`; });
  } else if (sectionKey === "muhurta") {
    const arr = formatMuhurta(entry.muhurta, showMidnight);
    if (arr.length) arr.forEach((s) => { html += `<p style="${bodyStyle}">${esc(s)}</p>`; });
    else html += `<p style="${bodyStyle}">No favourable timings</p>`;
  } else if (sectionKey === "activities") {
    (entry.activities || []).forEach((a) => {
      const text = typeof a === "string" ? a : String(a?.join?.(" ") ?? a);
      html += `<p style="${bodyStyle}">${esc(conv(text))}</p>`;
    });
  } else if (sectionKey === "inauspicious" && entry.inauspicious) {
    html += `<p style="${bodyStyle}">${esc(conv(entry.inauspicious))}</p>`;
  }


  return html ? `<div style="font-family: ${esc(typography.fontFamily)};">${html}</div>` : "";
}

export const FONT_FAMILIES = [
  "Times New Roman",
  "Arial",
  "Helvetica",
  "Georgia",
  "Garamond",
  "Verdana",
  "Cambria",
  "Calibri",
  "Consolas",
  "Courier New",
  ...TAMIL_FONTS,
];


export const TEXT_TRANSFORMS = [
  { value: "none", label: "None" },
  { value: "uppercase", label: "Uppercase" },
  { value: "lowercase", label: "Lowercase" },
  { value: "capitalize", label: "Capitalize" },
  { value: "small-caps", label: "Small caps" },
];
